/// <amd-module name="Wit/Filter/TextFilterProvider" />
import { contains } from "Wit/Common/Utilities/Array";
import { intersectPrimitives } from "Wit/Filter/Utilities/Array";
import { convertValueToDisplayString } from "Wit/Common/Utilities/Core";
import { IFilterProvider, IFilterDataSource, IFilter, IBoardFilterDataSource } from "Wit/Filter/FilterManager";
import {
    SearchCore,
    SearchStrategy,
    SubstringSearchStrategy,
    IndexedSearchStrategy,
    SearchAdapter,
    SearchableObject
} from "Wit/Filter/Search/Search";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { isWorkItemIdentityRef, isIdentityRef } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import * as Resources from "Wit/Filter/Resources";

export const SearchStrategySpecialCharacters: string[] = ["{", "(", "[", "!", "@", "#", "$", "%", "^", "&", "*", "~", "`", "'", '"'];

export abstract class BaseTextFilterProvider implements IFilterProvider {
    public static PROVIDER_TYPE = "text";

    private _searchValues: string[];
    private _searchCore: SearchCore<number>;
    private _searchAdapter: TextSearchAdapter;
    private _dataProvider: IFilterDataSource;
    private _updateRequired: boolean;

    constructor() {
        // Create a search strategy and bind it to the provider
        this._searchAdapter = this.getSearchAdapter();
        this._searchCore = new SearchCore<number>(this._createSearchStrategy(), this._searchAdapter);
        this._updateRequired = true;
    }

    /**
     * Return the search adapter to be used.
     */
    protected abstract getSearchAdapter(): TextSearchAdapter;

    public bind(provider: IFilterDataSource) {
        this._dataProvider = provider;
    }

    public setFilter(filter: IFilter) {
        this._searchValues = [];

        if (filter && filter.values && filter.values.length) {
            //Debug.assert((filter.values as string[]).every(value => typeof (value) === "string"), "Text filter only supports strings");

            this._searchValues = filter.values as string[];
        }
    }

    public getFilter(): IFilter {
        return { values: this._searchValues || [] };
    }

    public clearFilters() {
        this._searchValues = null;
    }

    public dataUpdated() {
        // Set update required so on the next search we will re-index the data.
        this._updateRequired = true;
    }

    public getMatchingItems(ids: number[]): number[] {
        //Debug.assert(this.isFiltering(), "must be filtering before calling getMatchingWorkItems");

        if (this._updateRequired) {
            this._searchAdapter.setDataSource(this._dataProvider);
            this._searchCore.clearStrategyStore();
            this._searchCore.addItems(this._searchAdapter.createSearchableObjects());
            this._updateRequired = false;
        }

        let results = this._searchCore.beginSearch(this._searchValues.join(" "));

        // Trim the results down to the intersection of the specified set and the full set that was found.
        // If no initial set is of work items is specified, return the full set of matched items.
        if (results && ids) {
            results = intersectPrimitives(ids, results);
        }

        return results;
    }

    public isFiltering(): boolean {
        return !!this._searchValues && this._searchValues.some(value => value !== "");
    }

    protected _createSearchStrategy(): SearchStrategy<number> {
        return new IndexedSearchStrategy<number>(null, {
            specialCharacters: SearchStrategySpecialCharacters,
            delimiter: /[\s\\/\.]/,
            comparer: (a: number, b: number) => {
                return a - b;
            }
        });
    }
}

export class TextFilterProvider extends BaseTextFilterProvider {
    protected getSearchAdapter(): TextSearchAdapter {
        return new TextSearchAdapter();
    }
}

export interface ITextSearchAdapter {
    /**
     * Set the data source for search.
     */
    setDataSource(dataSource: IFilterDataSource);
    /**
     * Return the data source.
     */
    getDataSource(): IFilterDataSource;
}

export class TextSearchAdapter extends SearchAdapter<number> implements ITextSearchAdapter {
    private _dataSource: IFilterDataSource;
    private _parentMap: Map<number, string> = new Map();

    public setDataSource(dataSource: IFilterDataSource) {
        this._dataSource = dataSource;
    }

    public getDataSource(): IFilterDataSource {
        return this._dataSource;
    }

    public createSearchableObjects(): SearchableObject<number>[] {
        const searchableObjects: SearchableObject<number>[] = [];

        const ids = this._dataSource.getIds();
        this.createParentMap();
        for (const id of ids) {
            const searchableObject = this.createSearchableObject(id);

            if (searchableObject) {
                searchableObjects.push(searchableObject);
            }
        }

        return searchableObjects;
    }

    public createSearchableObject(workItemId: number): SearchableObject<number> {
        //Debug.assertParamIsNumber(workItemId, "workItemId");

        // Build the searchable object.
        const searchableObject = new SearchableObject(workItemId, []);

        const fields = this._dataSource.getVisibleFields();
        for (const fieldName of fields) {
            const pagedFieldValue = this._dataSource.getValue(workItemId, fieldName);

            // Only index the data if it's not null and it's part of the visible set of fields.
            const type = typeof pagedFieldValue;

            if (type === "string") {
                searchableObject.addTerm(pagedFieldValue);
            } else if (type === "number") {
                searchableObject.addTerm(pagedFieldValue + "");

                if (fieldName === CoreFieldRefNames.Parent) {
                    const parentTitle = this._parentMap.get(pagedFieldValue);
                    if (parentTitle) {
                        searchableObject.addTerm(parentTitle);
                    }
                }
            } else if (
                pagedFieldValue &&
                isWorkItemIdentityRef(pagedFieldValue) &&
                pagedFieldValue.identityRef &&
                pagedFieldValue.identityRef.displayName
            ) {
                searchableObject.addTerm(pagedFieldValue.identityRef.displayName);
            } else if (pagedFieldValue && isIdentityRef(pagedFieldValue) && pagedFieldValue.displayName) {
                searchableObject.addTerm(pagedFieldValue.displayName);
            } else {
                if (pagedFieldValue == null) {
                    continue;
                } else {
                    const text = convertValueToDisplayString(pagedFieldValue);
                    if (text) {
                        searchableObject.addTerm(text);
                    }
                }
            }
        }

        return searchableObject;
    }

    public handleResults(results: number[], finished: boolean, query?: string) {}

    public handleError(message: string) {
        //Debug.fail("handleError for TextSearchAdapter called");
    }

    public handleClear() {}

    public isDataSetComplete(): boolean {
        return true;
    }

    public addMoreItems(addItemsCallback: (items: SearchableObject<number>[]) => void, searchCallback: () => void) {
        //Debug.fail("addMoreItems should never be called");
    }

    private createParentMap() {
        if (!this._dataSource.getVisibleFields().includes(CoreFieldRefNames.Parent)) {
            return;
        }

        const allParent = (this._dataSource as IBoardFilterDataSource)?.getParentItems?.();
        if (allParent?.length) {
            this._parentMap = new Map(allParent.map(item => [item.id, item.title]));
        }
    }
}

/**
 * This text filter provider override the empty value of Assigned To field value to match 'Unassigned' resource.
 */
export class TextFilterProviderWithUnassigned extends BaseTextFilterProvider {
    protected getSearchAdapter(): TextSearchAdapter {
        return new TextSearchAdapterTextFilterProviderWithUnassigned();
    }
}

class TextSearchAdapterTextFilterProviderWithUnassigned extends TextSearchAdapter {
    private _containsAssignedToMeColumn = false;

    public createSearchableObjects(): SearchableObject<number>[] {
        const datasource = this.getDataSource();
        this._containsAssignedToMeColumn = contains(datasource.getVisibleFields(), CoreFieldRefNames.AssignedTo);
        return super.createSearchableObjects();
    }

    public createSearchableObject(workItemId: number): SearchableObject<number> {
        const searchableObject = super.createSearchableObject(workItemId);
        const dataSource = this.getDataSource();
        if (this._containsAssignedToMeColumn && !dataSource.getValue(workItemId, CoreFieldRefNames.AssignedTo)) {
            searchableObject.addTerm(Resources.AssignedToEmptyText);
        }

        return searchableObject;
    }
}

export class SubstringTextFilterProvider extends TextFilterProviderWithUnassigned {
    protected _createSearchStrategy(): SearchStrategy<number> {
        return new SubstringSearchStrategy();
    }
}
